import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Mail, Globe, ArrowRight, Loader2, CheckCircle2, ArrowLeft } from "lucide-react";
import { Logo } from "../components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !websiteUrl) {
      toast.error("Bitte alle Felder ausfüllen");
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/forgot-password`, {
        email,
        website_url: websiteUrl,
      });
      if (res.data.success) {
        setSubmitted(true);
      }
    } catch (err) {
      const detail = err.response?.data?.detail || "Fehler bei der Anfrage";
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg border border-slate-100 p-8 max-w-md w-full text-center">
          <div className="w-14 h-14 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-5">
            <CheckCircle2 className="w-7 h-7 text-green-500" />
          </div>
          <h1 className="text-xl font-bold text-slate-900 mb-2" data-testid="forgot-success">
            Anfrage eingegangen
          </h1>
          <p className="text-slate-500 text-sm mb-6 leading-relaxed">
            Ihre Anfrage wird geprüft. Nach erfolgreicher Verifizierung erhalten Sie einen Link per E-Mail, um Ihr Passwort zurückzusetzen.
          </p>
          <Link
            to="/anmelden?mode=login"
            className="text-sm text-indigo-600 font-medium hover:underline"
          >
            Zurück zur Anmeldung
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-block">
            <Logo size="lg" clickable={false} />
          </Link>
        </div>

        <div className="bg-white rounded-2xl shadow-lg border border-slate-100 p-8">
          <div className="mb-6">
            <h1 className="text-xl font-bold text-slate-900 mb-1" data-testid="forgot-password-title">
              Passwort vergessen
            </h1>
            <p className="text-sm text-slate-500 leading-relaxed">
              Geben Sie Ihre E-Mail und die URL Ihrer Webseite ein. Nach Prüfung durch unser Team erhalten Sie einen Reset-Link.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="email" className="text-sm font-medium text-slate-700 mb-1.5 block">
                E-Mail-Adresse
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  data-testid="forgot-email"
                  id="email"
                  type="email"
                  placeholder="name@firma.de"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 h-11 rounded-xl"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="website" className="text-sm font-medium text-slate-700 mb-1.5 block">
                Ihre Webseite
              </Label>
              <div className="relative">
                <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  data-testid="forgot-website"
                  id="website"
                  type="text"
                  placeholder="meinefirma.de oder meinefirma.webgenerator.io"
                  value={websiteUrl}
                  onChange={(e) => setWebsiteUrl(e.target.value)}
                  className="pl-10 h-11 rounded-xl"
                />
              </div>
              <p className="text-xs text-slate-400 mt-1.5">
                Die Domain, die Sie bei der Bestellung angegeben haben.
              </p>
            </div>
            <Button
              data-testid="forgot-submit"
              type="submit"
              disabled={loading}
              className="w-full h-11 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-semibold"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>Anfrage senden <ArrowRight className="w-4 h-4 ml-1.5" /></>
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <Link
              to="/anmelden?mode=login"
              data-testid="back-to-login"
              className="text-sm text-indigo-600 font-medium hover:underline inline-flex items-center gap-1"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Zurück zur Anmeldung
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
